import { ObjectId } from 'bson';

const SAME_ORIGIN_PROBE = 'https://feathr.invalid';

/**
 * The in-app path to return to after OAuth. `state` comes from the URL, so a value that does not
 * decode, that is not a path in this app, or that the router cannot push gives `fallback`.
 */
export function getOAuthReturnPath(encodedState: string | null, fallback: string): string {
  if (!encodedState) {
    return fallback;
  }
  let path: string;
  try {
    path = atob(encodedState);
  } catch {
    return fallback;
  }
  // A path must start with one slash. "//host" is a protocol-relative URL to another site.
  if (!path.startsWith('/') || path.startsWith('//')) {
    return fallback;
  }
  const restIndex = path.search(/[?#]/);
  const pathname = restIndex === -1 ? path : path.slice(0, restIndex);
  const rest = restIndex === -1 ? '' : path.slice(restIndex);
  try {
    // The router decodes the pathname and then pushes it. Check the origin of that decoded value.
    const decodedPathname = decodeURI(pathname);
    // The URL parser reads "\" as "/" and removes tabs, so "/\host" is also another site.
    if (new URL(`${decodedPathname}${rest}`, SAME_ORIGIN_PROBE).origin !== SAME_ORIGIN_PROBE) {
      return fallback;
    }
  } catch {
    return fallback;
  }
  return path;
}

/**
 * The account that started the OAuth flow, from the first segment of the return path. Blackbaud
 * redirects to a path with no account, so the app adds the user's default account instead.
 */
export function getOAuthAccountId(returnPath: string): string | undefined {
  const accountId = returnPath.split(/[/?#]/)[1];
  return accountId && ObjectId.isValid(accountId) ? accountId : undefined;
}
